/**
 * Best Practice Auth Service
 * Optimized authentication flow with proper logout and user validation
 */

import axios, { AxiosInstance } from 'axios';
import { authStore } from './authStore';

const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace(/\/$/, '');
const authApi: AxiosInstance = axios.create({
  baseURL: `${apiBase}/api`,
  withCredentials: true,
  timeout: 3000, // Fast timeout for auth calls
});

interface LoginResponse {
  token: string;
  userType: string;
  audience: string;
}

interface UserInfo {
  id: number;
  email: string;
  firstname: string;
  lastname: string;
  enterpriseName: string;
  telephone: string;
  address?: string;
  city?: string;
  country?: string;
  ice?: string;
  identifiantFiscal?: string;
  siren?: string;
  siret?: string;
  companyNumber?: string;
  nif?: string;
  codiceFiscale?: string;
  uid?: string;
  steuerNumber?: string;
  rcsNumber?: string;
  kvkNumber?: string;
  defaultInvoiceLegalMentions?: string;
  vatNumber?: string;
  iban?: string;
  bankName?: string;
  role: string;
}

/**
 * Clear all cookies manually (for logout)
 * This ensures cookies are cleared even if backend call fails
 */
function clearAllCookies(): void {
  // Clear refresh_token cookie for all possible paths
  const paths = ['/', '/api', '/api/auth'];
  paths.forEach(path => {
    document.cookie = `refresh_token=; Path=${path}; Max-Age=0; SameSite=Strict`;
    document.cookie = `refresh_token=; Path=${path}; Max-Age=0; SameSite=Lax`;
  });
  // Also try to clear with domain
  const hostname = window.location.hostname;
  if (hostname !== 'localhost') {
    document.cookie = `refresh_token=; Domain=${hostname}; Path=/; Max-Age=0`;
  }
}

/**
 * Initialize authentication on app start
 * Only attempts refresh if not already authenticated
 */
export async function initializeAuth(): Promise<void> {
  // If already initialized and authenticated, skip
  const currentState = authStore.getState();
  if (currentState.isInitialized && currentState.isAuthenticated) {
    return;
  }

  try {
    const response = await authApi.post<{ token: string }>(
      '/auth/refresh',
      {},
      { headers: { 'X-Client': 'web' } }
    );

    if (response.data?.token) {
      authStore.setAccessToken(response.data.token);

      // Fetch user info and validate it matches
      try {
        await fetchUserInfo();
      } catch (err) {
        // If user fetch fails, clear auth
        console.error('Failed to fetch user info after refresh:', err);
        authStore.clear();
        clearAllCookies();
      }
    } else {
      authStore.clear();
    }
  } catch (error) {
    // No valid refresh token - user needs to login
    authStore.clear();
    clearAllCookies(); // Ensure cookies are cleared on refresh failure
  } finally {
    authStore.setInitialized(true);
  }
}

/**
 * Fetch user info (non-blocking)
 */
export async function fetchUserInfo(): Promise<void> {
  const api = (await import('./api')).default;
  const userResponse = await api.get<UserInfo>('/users/me');
  authStore.setUser({
    id: userResponse.data.id,
    email: userResponse.data.email,
    userType: userResponse.data.role,
    firstname: userResponse.data.firstname,
    lastname: userResponse.data.lastname,
    enterpriseName: userResponse.data.enterpriseName,
    telephone: userResponse.data.telephone,
    address: userResponse.data.address,
    city: userResponse.data.city,
    country: userResponse.data.country,
    ice: userResponse.data.ice,
    identifiantFiscal: userResponse.data.identifiantFiscal,
    siren: userResponse.data.siren,
    siret: userResponse.data.siret,
    companyNumber: userResponse.data.companyNumber,
    nif: userResponse.data.nif,
    codiceFiscale: userResponse.data.codiceFiscale,
    uid: userResponse.data.uid,
    steuerNumber: userResponse.data.steuerNumber,
    rcsNumber: userResponse.data.rcsNumber,
    kvkNumber: userResponse.data.kvkNumber,
    defaultInvoiceLegalMentions: userResponse.data.defaultInvoiceLegalMentions,
    vatNumber: userResponse.data.vatNumber,
    iban: userResponse.data.iban,
    bankName: userResponse.data.bankName,
  });
}

/**
 * Login user - OPTIMIZED: Uses response data immediately
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  // Clear any existing cookies before login to prevent conflicts
  clearAllCookies();

  const response = await authApi.post<LoginResponse>(
    '/auth/login',
    { email, password },
    { headers: { 'X-Client': 'web' } }
  );

  if (response.data?.token) {
    // Set token and userType immediately from login response (no waiting)
    authStore.setAccessToken(response.data.token);
    authStore.setUser({
      id: null, // Will be fetched in background
      email: email,
      userType: response.data.userType,
    });
    authStore.setInitialized(true); // Mark as initialized immediately

    // Fetch full user info in background and validate it matches
    fetchUserInfo().catch((err) => {
      console.error('Failed to fetch user info:', err);
      // Don't fail login if user info fetch fails - we already have userType
    });
  }

  return response.data;
}

/**
 * Logout user - COMPREHENSIVE: Clears all state and cookies
 */
export async function logout(): Promise<void> {
  try {
    await authApi.post('/auth/logout', {}, { headers: { 'X-Client': 'web' } });
  } catch (error) {
    // Continue with logout even if server call fails
    console.error('Logout error:', error);
  } finally {
    // Always clear local state immediately
    authStore.clear();
    // Force clear all cookies manually (ensures cleanup even if backend fails)
    clearAllCookies();
    // Note: clear() already sets isInitialized to true, so ProtectedRoute will redirect to login
  }
}

/**
 * Refresh access token using httpOnly cookie
 * Validates user info matches after refresh
 */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    const response = await authApi.post<{ token: string }>(
      '/auth/refresh',
      {},
      { headers: { 'X-Client': 'web' } }
    );

    if (response.data?.token) {
      const previousUser = authStore.getState().user;
      authStore.setAccessToken(response.data.token);

      // Fetch user info to validate it matches
      try {
        await fetchUserInfo();
        const newUser = authStore.getState().user;

        // If user changed (shouldn't happen but safety check), clear auth
        if (previousUser && newUser && previousUser.email !== newUser.email) {
          console.warn('User mismatch after refresh - clearing auth');
          authStore.clear();
          clearAllCookies();
          return null;
        }
      } catch (err) {
        console.error('Failed to fetch user info after refresh:', err);
        authStore.clear();
        clearAllCookies();
        return null;
      }

      return response.data.token;
    }
    return null;
  } catch (error) {
    authStore.clear();
    clearAllCookies();
    return null;
  }
}
